import { TranslateModule, TranslateService } from '@ngx-translate/core';
import {
  afterNextRender,
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  ElementRef,
  inject,
  signal,
  viewChild,
} from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router, RouterOutlet } from '@angular/router';
import {
  GroupedViewRequirementIntake,
  IntakeStatus,
  RequirementIntake,
  RequirementVerdict,
  SaveVerdictRequest,
  ViewService,
} from 'api';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import {
  combineLatest,
  debounceTime,
  distinctUntilChanged,
  EMPTY,
  filter,
  finalize,
  startWith,
  switchMap,
  tap,
} from 'rxjs';
import { ApplicabilityChangeEvent } from 'component/details/requirement-intake/requirements-detail/applicability-section/applicability-section.component';
import { RequirementIntakeCommandService } from 'component/details/requirement-intake/requirement-intake-command.service';
import { RequirementTreeComponent } from 'component/details/requirement-intake/requirement-tree/requirement-tree.component';
import { RequirementsDetailComponent } from 'component/details/requirement-intake/requirements-detail/requirements-detail.component';
import { RequirementIntakeEventsService } from 'component/details/requirement-intake/requirement_intake_events_service';
import { ChipComponent } from 'common/designsystem/chip/chip.component';

interface RequirementIntakeDataState {
  readonly isLoading: boolean;
  readonly hasError: boolean;
  readonly groups: ReadonlyArray<GroupedViewRequirementIntake>;
}

const ALL_REQUIREMENTS_PAGE_SIZE = 250;

@Component({
  templateUrl: 'requirement-intake.component.html',
  styleUrls: ['./requirement-intake.component.scss'],
  imports: [
    TranslateModule,
    RequirementTreeComponent,
    RequirementsDetailComponent,
    ChipComponent,
    RouterOutlet,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RequirementIntakeComponent {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);
  private readonly viewService = inject(ViewService);
  private readonly commandService = inject(RequirementIntakeCommandService);
  private readonly translateService = inject(TranslateService);
  private readonly requirementIntakeEventsService = inject(RequirementIntakeEventsService);

  protected readonly revisionId = signal<number | undefined>(undefined);
  protected readonly selectedRequirementId = signal<number | undefined>(undefined);

  protected readonly groupedRequirements = signal<ReadonlyArray<GroupedViewRequirementIntake>>([]);
  protected readonly isSubmitPending = signal(false);
  protected readonly actionMessage = signal<string | undefined>(undefined);

  private readonly requirementDataState = signal<RequirementIntakeDataState>({
    isLoading: false,
    hasError: false,
    groups: [],
  });
  private readonly isRequirementIntakeReloadInProgress = signal(false);
  private readonly isRequirementIntakeReloadQueued = signal(false);

  protected readonly isRequirementsLoading = computed(() => this.requirementDataState().isLoading);
  protected readonly hasRequirementsError = computed(() => this.requirementDataState().hasError);
  protected readonly selectedRequirement = computed<RequirementIntake | undefined>(() => {
    const selectedRequirementId = this.selectedRequirementId();

    if (selectedRequirementId === undefined) {
      return undefined;
    }

    return this.groupedRequirements()
      .flatMap((group) => group.items)
      .find((requirement) => requirement.id === selectedRequirementId);
  });
  protected readonly isCollapsed = signal(false);
  protected readonly isMobileMode = signal(false);
  protected readonly hasWorkspaceChildRoute = signal(false);
  private readonly workspacePanelRef = viewChild<ElementRef<HTMLElement>>('workspacePanel');
  private readonly treeScrollContainerRef =
    viewChild<ElementRef<HTMLElement>>('treeScrollContainer');
  protected readonly showFormTopShadow = signal(false);
  protected readonly showFormBottomShadow = signal(false);
  protected readonly showTreeTopShadow = signal(false);
  protected readonly showTreeBottomFade = signal(false);

  constructor() {
    toObservable(this.revisionId)
      .pipe(
        distinctUntilChanged(),
        tap((revisionId) => {
          this.loadRequirementIntakeFromBackend(revisionId, true);
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();

    toObservable(this.revisionId)
      .pipe(
        switchMap((revisionId) => {
          if (revisionId === undefined) {
            return EMPTY;
          }
          return this.requirementIntakeEventsService.listenForRevision(revisionId);
        }),
        debounceTime(500),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((event) => {
        if (event.revisionId !== this.revisionId()) {
          return;
        }

        this.loadRequirementIntakeFromBackend(event.revisionId, false);
      });

    effect(() => {
      this.groupedRequirements.set(this.requirementDataState().groups);
    });

    effect(() => {
      this.groupedRequirements();
      this.isCollapsed();
      queueMicrotask(() => this.onTreeScroll());
    });

    effect(() => {
      const selectedRequirementId = this.selectedRequirementId();
      const hasWorkspaceChildRoute = this.hasWorkspaceChildRoute();

      if (selectedRequirementId === undefined || hasWorkspaceChildRoute) {
        return;
      }

      queueMicrotask(() => this.scrollWorkspaceToTop());
    });

    effect(() => {
      this.selectedRequirement()?.id;
      this.isRequirementsLoading();
      this.hasWorkspaceChildRoute();
      queueMicrotask(() => this.onWorkspaceScroll());
    });

    combineLatest([this.route.paramMap, this.route.queryParamMap])
      .pipe(
        tap(([urlParamMap, queryParamMap]) => {
          const revisionId = Number(urlParamMap.get('revisionId'));

          if (Number.isInteger(revisionId)) {
            if (this.revisionId() !== revisionId) {
              this.selectedRequirementId.set(undefined);
              this.requirementDataState.set({
                isLoading: false,
                hasError: false,
                groups: [],
              });
              this.isSubmitPending.set(false);
              this.actionMessage.set(undefined);
            }

            this.revisionId.set(revisionId);
          }

          const requirementIdParam = queryParamMap.get('requirementId');
          const requirementId =
            requirementIdParam === null ? undefined : Number(requirementIdParam);

          if (
            requirementId !== undefined &&
            Number.isInteger(requirementId) &&
            this.selectedRequirementId() !== requirementId
          ) {
            this.selectedRequirementId.set(requirementId);
          }
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();

    this.router.events
      .pipe(
        filter((event): event is NavigationEnd => event instanceof NavigationEnd),
        startWith(null),
        tap(() => this.hasWorkspaceChildRoute.set(this.route.snapshot.firstChild !== null)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();

    afterNextRender(() => {
      const isMobileMode = window.matchMedia('(max-width: 1120px)');

      const setIsMobileMode = (event: MediaQueryListEvent | MediaQueryList): void => {
        if (event.matches) {
          this.isMobileMode.set(true);
          this.isCollapsed.set(true);
        } else {
          this.isMobileMode.set(false);
          this.isCollapsed.set(false);
        }
      };

      setIsMobileMode(isMobileMode);
      isMobileMode.addEventListener('change', setIsMobileMode);
      window.addEventListener('resize', this.onResize);
      this.onTreeScroll();
      this.onWorkspaceScroll();

      this.destroyRef.onDestroy(() => {
        isMobileMode.removeEventListener('change', setIsMobileMode);
        window.removeEventListener('resize', this.onResize);
      });
    });
  }

  protected async openRequirementIntake(id: number): Promise<boolean> {
    this.selectedRequirementId.set(id);
    this.actionMessage.set(undefined);
    return await this.router.navigate(['../', this.revisionId()], {
      relativeTo: this.route.parent,
      queryParams: { requirementId: id },
    });
  }

  protected async reloadRequirementIntake(_requirementId: number): Promise<void> {
    this.loadRequirementIntakeFromBackend(this.revisionId(), false);
  }

  protected onApplicabilityConfirmed(event: ApplicabilityChangeEvent): void {
    const { requirementId, model } = event;
    const justification = model.notApplicableJustification?.trim();

    if (this.isSubmitPending() || (model.applicability === false && justification === undefined)) {
      return;
    }

    this.isSubmitPending.set(true);
    this.actionMessage.set(undefined);

    if (model.applicability === false) {
      this.commandService
        .confirmNotApplicable(requirementId, justification!)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: (_result) => {
            this.updateRequirement(requirementId, (requirement) => ({
              ...requirement,
              intakeStatus: IntakeStatus.NotApplicable,
              applicability: false,
              justification,
            }));
            this.isSubmitPending.set(false);
          },
          error: () => {
            this.actionMessage.set(
              this.translateService.instant('requirementIntake.messages.applicableSaveError'),
            );
            this.isSubmitPending.set(false);
          },
        });
    } else {
      this.commandService
        .confirmApplicable(requirementId)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: (_result) => {
            this.updateRequirement(requirementId, (requirement) => ({
              ...requirement,
              intakeStatus: IntakeStatus.Incomplete,
              applicability: true,
              justification: undefined,
            }));
            this.isSubmitPending.set(false);
          },
          error: () => {
            this.actionMessage.set(
              this.translateService.instant('requirementIntake.messages.applicableSaveError'),
            );
            this.isSubmitPending.set(false);
          },
        });
    }
  }

  protected saveVerdict(request: { requirementId: number; command: SaveVerdictRequest }): void {
    if (this.isSubmitPending()) {
      return;
    }

    this.isSubmitPending.set(true);
    this.actionMessage.set(undefined);

    this.commandService
      .saveVerdict(request.requirementId, request.command)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.updateRequirement(request.requirementId, (requirement) => ({
            ...requirement,
            intakeStatus: IntakeStatus.Decided,
            verdict: this.buildUpdatedVerdict(requirement, request.command),
          }));

          this.isSubmitPending.set(false);
        },
        error: () => {
          this.actionMessage.set(
            this.translateService.instant('requirementIntake.messages.verdictSaveError'),
          );
          this.isSubmitPending.set(false);
        },
      });
  }

  private updateRequirement(
    requirementId: number,
    updateRequirement: (requirement: RequirementIntake) => RequirementIntake,
  ): void {
    this.requirementDataState.update((current) => ({
      ...current,
      groups: current.groups.map((group) => ({
        ...group,
        items: group.items.map((requirement) =>
          requirement.id === requirementId ? updateRequirement(requirement) : requirement,
        ),
      })),
    }));
  }

  private buildUpdatedVerdict(
    requirement: RequirementIntake,
    command: SaveVerdictRequest,
  ): RequirementVerdict {
    const proposedResult = requirement.verdict?.proposedResult;
    const trimmedJustification = command.justification?.trim();
    const isOverride = proposedResult !== undefined && proposedResult !== command.result;

    return {
      ...requirement.verdict,
      proposedResult,
      rationale: requirement.verdict?.rationale,
      finalResult: command.result,
      resultOverride:
        proposedResult === undefined ? command.result : isOverride ? command.result : undefined,
      justification:
        trimmedJustification && trimmedJustification.length > 0 ? trimmedJustification : undefined,
      proposed: false,
      decided: true,
    };
  }

  private mergeRequirementGroups(
    currentGroups: ReadonlyArray<GroupedViewRequirementIntake>,
    backendGroups: ReadonlyArray<GroupedViewRequirementIntake>,
  ): ReadonlyArray<GroupedViewRequirementIntake> {
    const currentGroupsById = new Map(currentGroups.map((group) => [group.id, group]));

    return backendGroups.map((backendGroup) => {
      const currentGroup = currentGroupsById.get(backendGroup.id);

      if (!currentGroup) {
        return backendGroup;
      }

      const mergedItems = this.mergeRequirements(currentGroup.items, backendGroup.items);

      if (currentGroup.groupName === backendGroup.groupName && mergedItems === currentGroup.items) {
        return currentGroup;
      }

      return {
        ...currentGroup,
        ...backendGroup,
        items: mergedItems,
      };
    });
  }

  private mergeRequirements(
    currentRequirements: Array<RequirementIntake>,
    backendRequirements: Array<RequirementIntake>,
  ): Array<RequirementIntake> {
    const currentRequirementsById = new Map(
      currentRequirements.map((requirement) => [requirement.id, requirement]),
    );

    let changed = currentRequirements.length !== backendRequirements.length;

    const mergedRequirements = backendRequirements.map((backendRequirement) => {
      const currentRequirement = currentRequirementsById.get(backendRequirement.id);

      if (!currentRequirement) {
        changed = true;
        return backendRequirement;
      }

      if (this.areRequirementRefreshFieldsEqual(currentRequirement, backendRequirement)) {
        return currentRequirement;
      }

      changed = true;
      return {
        ...currentRequirement,
        intakeStatus: backendRequirement.intakeStatus,
        evidences: backendRequirement.evidences,
        applicability: backendRequirement.applicability,
        justification: backendRequirement.justification,
        verdict: backendRequirement.verdict,
      };
    });

    return changed ? mergedRequirements : currentRequirements;
  }

  private areRequirementRefreshFieldsEqual(
    currentRequirement: RequirementIntake,
    backendRequirement: RequirementIntake,
  ): boolean {
    return (
      currentRequirement.intakeStatus === backendRequirement.intakeStatus &&
      currentRequirement.applicability === backendRequirement.applicability &&
      currentRequirement.justification === backendRequirement.justification &&
      JSON.stringify(currentRequirement.verdict) === JSON.stringify(backendRequirement.verdict) &&
      JSON.stringify(currentRequirement.evidences) === JSON.stringify(backendRequirement.evidences)
    );
  }

  private loadRequirementIntakeFromBackend(
    revisionId: number | undefined,
    initialLoading: boolean,
  ): void {
    if (revisionId === undefined) {
      this.requirementDataState.update((_current) => ({
        groups: [],
        isLoading: false,
        hasError: false,
      }));
      return;
    }

    if (this.isRequirementIntakeReloadInProgress()) {
      this.isRequirementIntakeReloadQueued.set(true);
      return;
    }

    this.isRequirementIntakeReloadInProgress.set(true);

    this.requirementDataState.update((current) => ({
      ...current,
      isLoading: initialLoading,
      hasError: false,
    }));

    this.viewService
      .getGroupedRequirements(revisionId, 0, ALL_REQUIREMENTS_PAGE_SIZE)
      .pipe(
        finalize(() => {
          this.isRequirementIntakeReloadInProgress.set(false);

          if (this.isRequirementIntakeReloadQueued()) {
            this.isRequirementIntakeReloadQueued.set(false);
            this.loadRequirementIntakeFromBackend(revisionId, false);
          }
        }),
      )
      .subscribe({
        next: (result) => {
          const backendGroups = result.items ?? [];

          this.requirementDataState.update((current) => ({
            isLoading: false,
            hasError: false,
            groups: this.mergeRequirementGroups(current.groups, backendGroups),
          }));
        },
        error: () => {
          this.requirementDataState.update((current) => ({
            ...current,
            isLoading: false,
            hasError: true,
          }));
        },
      });
  }

  protected toggle() {
    this.isCollapsed.set(!this.isCollapsed());
  }

  protected async openSelfDeclaration(): Promise<boolean> {
    this.selectedRequirementId.set(undefined);
    return await this.router.navigate(['self-declaration'], {
      relativeTo: this.route,
      queryParams: {
        embedded: true,
      },
    });
  }

  protected onWorkspaceScroll(): void {
    const panel = this.workspacePanelRef()?.nativeElement;
    if (!panel) {
      this.showFormTopShadow.set(false);
      this.showFormBottomShadow.set(false);
      return;
    }

    const hasOverflow = panel.scrollHeight - panel.clientHeight > 1;
    const isAtTop = panel.scrollTop <= 1;
    const isAtBottom = panel.scrollTop + panel.clientHeight >= panel.scrollHeight - 1;

    this.showFormTopShadow.set(hasOverflow && !isAtTop);
    this.showFormBottomShadow.set(hasOverflow && !isAtBottom);
  }

  protected onTreeScroll(): void {
    const treeContainer = this.treeScrollContainerRef()?.nativeElement;
    if (!treeContainer || this.isCollapsed()) {
      this.showTreeTopShadow.set(false);
      this.showTreeBottomFade.set(false);
      return;
    }

    const hasOverflow = treeContainer.scrollHeight - treeContainer.clientHeight > 1;
    const isAtTop = treeContainer.scrollTop <= 1;
    const isAtBottom =
      treeContainer.scrollTop + treeContainer.clientHeight >= treeContainer.scrollHeight - 1;

    this.showTreeTopShadow.set(hasOverflow && !isAtTop);
    this.showTreeBottomFade.set(hasOverflow && !isAtBottom);
  }

  private readonly onResize = (): void => {
    this.onTreeScroll();
    this.onWorkspaceScroll();
  };

  private scrollWorkspaceToTop(): void {
    this.workspacePanelRef()?.nativeElement.scrollTo({ top: 0, left: 0, behavior: 'auto' });
    this.onWorkspaceScroll();
  }
}
